import React, { useState, useEffect } from 'react';
import API from '../utils/gasClient';
import { Loading } from '../components/Loading';

export const EventsListPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const eventsList = await API.getActiveEvents();
        setEvents(eventsList);
        setLoading(false);
      } catch (err) {
        setError('Não foi possível carregar os eventos. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleEventClick = (event) => {
    window.location.href = `/${event.eventSlug}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="text-center bg-white rounded-xl p-6 shadow-xl max-w-sm">
          <p className="text-red-600 mb-3 font-semibold">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary text-white rounded-full hover:bg-primary-hover transition-all duration-300 font-display font-bold text-sm uppercase"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="text-center bg-white rounded-xl p-8 shadow-xl max-w-md">
          <div className="text-5xl mb-3">🎉</div>
          <h2 className="text-2xl font-display font-bold text-gray-800 mb-2">
            Em breve...
          </h2>
          <p className="text-gray-600">
            Nenhum evento ativo no momento.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-extrabold text-primary mb-2 tracking-tight">
            PÉ DESCALÇO CASTELO
          </h1>
          <div className="h-1 w-24 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto mb-2"></div>
          <p className="text-lg text-gray-700 font-semibold">
            Escolha seu evento! 🎟️
          </p>
        </div>

        {/* Grid - CARDS LADO A LADO */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <div
              key={event.eventId}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
              onClick={() => handleEventClick(event)}
            >
              {/* Imagem */}
              <div className="relative h-48 overflow-hidden bg-gray-100">
                {event.imageId ? (
                  <img
                    src={`https://drive.google.com/thumbnail?id=${event.imageId}&sz=w500`}
                    alt={event.eventName}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onLoad={(e) => {
                      e.target.parentElement.classList.remove('bg-gray-100');
                    }}
                    onError={(e) => {
                      if (e.target.src.includes('thumbnail')) {
                        e.target.src = `https://drive.google.com/uc?export=view&id=${event.imageId}`;
                      } else if (e.target.src.includes('uc?export=view')) {
                        e.target.src = `https://lh3.googleusercontent.com/d/${event.imageId}`;
                      } else {
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        parent.innerHTML = `
                          <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20 p-4">
                            <div class="text-4xl mb-2">🖼️</div>
                            <p class="text-xs text-gray-600 text-center">Erro ao carregar</p>
                          </div>
                        `;
                      }
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
                    <div className="text-5xl mb-2">📸</div>
                    <p className="text-xs text-gray-600">Sem imagem</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Conteúdo */}
              <div className="p-5">
                <h2 className="text-xl font-display font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                  {event.eventName}
                </h2>
                
                {event.subtitle && (
                  <p className="text-gray-600 mb-3 text-sm line-clamp-2">
                    {event.subtitle}
                  </p>
                )}

                {/* Informações */}
                <div className="space-y-2 mb-4">
                  {event.eventDate && (
                    <div className="flex items-center text-gray-700 text-sm">
                      <span className="mr-2">📅</span>
                      <span className="font-medium">{formatDate(event.eventDate)}</span>
                    </div>
                  )}
                  
                  {event.eventTime && (
                    <div className="flex items-center text-gray-700 text-sm">
                      <span className="mr-2">🕐</span>
                      <span className="font-medium">{event.eventTime}</span>
                    </div>
                  )}
                  
                  {event.eventLocation && (
                    <div className="flex items-center text-gray-700 text-sm">
                      <span className="mr-2">📍</span>
                      <span className="font-medium truncate">{event.eventLocation}</span>
                    </div>
                  )}
                </div>

                {/* Botão */}
                <button className="w-full py-3 px-4 rounded-full font-display font-bold text-sm text-white bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary-light transition-all duration-300 shadow-md hover:shadow-lg uppercase tracking-wide">
                  Ver Ingressos →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function formatDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
}
